import React from "react";
import PosterArt from "./PosterArt";
import Wordmark from "./Wordmark";
import SignalCompare from "./SignalCompare";

// Relational Taste Match — NOT a personal score. Compact, visual, share-ready
// (compositionally suitable for later 9:16 export). Retains TASTE TEST DNA.

const CONTRAST_LABEL = {
  pace: "Pace", tone_dark: "Tone", optimism: "Outlook", tension: "Tension", moral_grey: "Morality",
  char_focus: "Focus", world_scale: "Scale", world_rules: "World logic", nar_linear: "Structure",
  stylization: "Style", spectacle: "Spectacle", atmosphere: "Atmosphere", cog_demand: "Demand",
  intensity: "Intensity", g_open: "Openness", g_challenge: "Challenge", g_depth: "Systems",
  temporal: "Setting", world_real: "Realism", sensory: "Sensory", ensemble: "Cast", emo_demand: "Feeling",
  interpretive: "Ambiguity",
};

export default function TasteMatch({ match, challengerHandle, onNext, onRestart, onGuessMyTaste }) {
  if (!match || !match.sufficient) {
    return (
      <div data-testid="taste-match" className="relative min-h-[100dvh] w-full max-w-[520px] mx-auto flex flex-col items-center justify-center px-8 text-center gap-5">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-subtle">Taste Match</p>
        <p className="font-display font-bold text-lg text-bone leading-snug">
          {match?.message || "Not enough taste data yet to compare."}
        </p>
        <button data-testid="match-restart" onClick={onRestart}
          className="font-body text-xs tracking-[0.18em] uppercase text-white/60 border border-white/15 rounded-full px-5 py-2.5 hover:text-white">
          Take the test
        </button>
      </div>
    );
  }

  return (
    <div data-testid="taste-match" className="relative min-h-[100dvh] w-full max-w-[520px] mx-auto overflow-x-hidden">
      <div className="relative z-10 px-5 pt-[calc(env(safe-area-inset-top)+22px)] pb-40">
        <div className="flex items-center justify-between">
          <Wordmark variant="horizontal" className="h-3.5" />
          <span className="font-mono text-[10px] tracking-[0.2em] text-subtle uppercase">Taste Match</span>
        </div>

        {/* The hero number */}
        <div className="mt-10 text-center">
          <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-silver mb-3">
            You vs {challengerHandle || "your challenger"}
          </p>
          <div
            data-testid="match-pct"
            className="font-display font-black leading-none tracking-[-0.04em]"
            style={{
              fontSize: "clamp(84px, 30vw, 150px)",
              background: "linear-gradient(120deg, var(--tt-left), var(--tt-right))",
              WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent",
            }}
          >
            {match.matchPct}%
          </div>
          <p className="font-display font-bold uppercase tracking-[0.1em] text-bone text-sm mt-1">Taste Match</p>
        </div>

        {/* Dual-marker signal comparison — grounds Shared DNA / Civil Wars */}
        {match.signalCompare?.length >= 3 && (
          <section className="mt-12">
            <SignalCompare
              rows={match.signalCompare}
              youLabel="You"
              themLabel={challengerHandle || "Them"}
            />
          </section>
        )}

        {/* Shared DNA */}
        {match.sharedDNA?.length > 0 && (
          <section className="mt-10" data-testid="shared-dna">
            <p className="font-mono text-[10px] font-bold tracking-[0.28em] uppercase text-subtle mb-3">Shared DNA</p>
            <div className="space-y-2.5">
              {match.sharedDNA.map((d, i) => (
                <div key={i} className="flex items-start gap-3 rounded-xl bg-white/[0.05] border border-white/10 px-3.5 py-3">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ background: "linear-gradient(120deg, var(--tt-left), var(--tt-right))" }} />
                  <p className="font-body text-[13px] text-bone leading-snug">{d.text}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Civil Wars */}
        {match.civilWars?.length > 0 && (
          <section className="mt-10" data-testid="civil-wars">
            <p className="font-mono text-[10px] font-bold tracking-[0.28em] uppercase text-subtle mb-3">Your civil wars</p>
            <div className="space-y-3">
              {match.civilWars.map((c, i) => (
                <div key={i} className="rounded-2xl overflow-hidden border border-white/12 bg-white/[0.03]">
                  {c.contrast && (
                    <div className="px-3 pt-2.5 pb-1 font-mono text-[9px] tracking-[0.2em] uppercase text-subtle">
                      {CONTRAST_LABEL[c.contrast] || "Split"} split
                    </div>
                  )}
                  <div className="grid grid-cols-2">
                    <div className="relative">
                      <div className="relative aspect-[2/3]">
                        <PosterArt work={c.youKept} showMeta={false} />
                        <span className="absolute inset-x-0 top-0 h-1" style={{ background: "var(--tt-left)" }} />
                      </div>
                      <div className="px-3 py-2">
                        <p className="font-mono text-[9px] tracking-[0.2em] uppercase" style={{ color: "var(--tt-left)" }}>You kept</p>
                        <p className="font-display font-bold text-[13px] text-bone leading-tight line-clamp-2">{c.youKept.title}</p>
                      </div>
                    </div>
                    <div className="relative border-l border-white/10">
                      <div className="relative aspect-[2/3]">
                        <PosterArt work={c.theyKept} showMeta={false} />
                        <span className="absolute inset-x-0 top-0 h-1" style={{ background: "var(--tt-right)" }} />
                      </div>
                      <div className="px-3 py-2 text-right">
                        <p className="font-mono text-[9px] tracking-[0.2em] uppercase" style={{ color: "var(--tt-right)" }}>They kept</p>
                        <p className="font-display font-bold text-[13px] text-bone leading-tight line-clamp-2">{c.theyKept.title}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Bridge pick */}
        {match.bridgePick && (
          <section className="mt-10" data-testid="bridge-pick">
            <p className="font-mono text-[10px] font-bold tracking-[0.28em] uppercase text-subtle mb-3">Your bridge pick</p>
            <div className="flex items-center gap-4 rounded-2xl overflow-hidden bg-gradient-to-br from-white/[0.09] to-white/[0.03] border border-white/12">
              <div className="relative w-20 shrink-0 aspect-[2/3]">
                <PosterArt work={match.bridgePick} showMeta={false} />
              </div>
              <div className="flex-1 py-3 pr-4">
                <h4 className="font-display font-black text-base text-bone leading-tight">{match.bridgePick.title}</h4>
                <p className="font-body text-[12px] text-silver leading-snug mt-1">The one you'd both keep. Watch it together.</p>
              </div>
            </div>
          </section>
        )}
      </div>

      {/* Second transmission — one dominant CTA, plus the restrained GUESS MY
          TASTE unlock (a reward for reaching a real Match, not a second
          front door — never shown before Match itself is sufficient). */}
      <div className="fixed inset-x-0 bottom-0 z-20 max-w-[520px] mx-auto px-5 pb-[calc(env(safe-area-inset-bottom)+18px)] pt-6 bg-gradient-to-t from-canvas via-canvas/95 to-transparent">
        {match.guessMyTasteEnabled && onGuessMyTaste && (
          <div className="mb-2.5" data-testid="guess-my-taste-unlock">
            {/* The unlock moment: the social layer just became playable.
                Restrained by design — an eyebrow, not confetti, no "LEVEL 2",
                no achievement language. */}
            <p className="mb-1.5 text-center font-mono text-[9.5px] font-bold tracking-[0.28em] uppercase"
               style={{ color: "var(--tt-right)" }}>
              Unlocked
            </p>
            <button
              type="button"
              data-testid="guess-my-taste-cta"
              onClick={onGuessMyTaste}
              className="w-full rounded-full py-3.5 font-display font-bold uppercase tracking-[0.06em] text-xs text-bone border border-white/25 bg-white/[0.06] hover:bg-white/[0.10] active:scale-[0.99] transition-all"
            >
              Guess my taste
            </button>
            <p className="mt-1.5 text-center font-body text-[11px] text-white/40">
              Think you know what they kept?
            </p>
          </div>
        )}
        <button
          type="button"
          data-testid="second-challenge-cta"
          onClick={onNext}
          className="w-full rounded-full py-4 font-display font-black uppercase tracking-[0.06em] text-sm text-[#05070a] active:scale-[0.99] transition-transform"
          style={{ background: "linear-gradient(90deg, var(--tt-left), var(--tt-right))" }}
        >
          Challenge someone else
        </button>
      </div>
    </div>
  );
}
